import {
  Grid,
  TextField,
  Collapse,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import {
  blInitialValues,
  placeholderConsignee,
  placeholderNotify,
  placeholderShipper,
  terminoFleteOptions,
  tipoBlOptions,
} from "../constants/form-constants";
import { useState, useEffect } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconDeviceFloppy,
  IconPlus,
} from "@tabler/icons-react";
import NewContainerForm from "./NewContainerForm";
import ContainersList from "./ContainersList";
import {
  BLValues,
  CreateBLPayload,
} from "../../../interfaces/bl-export/request.bl.interface";
import { useFormik } from "formik";
import { BlSchema } from "../constants/form.shemas";
import { useTextfieldStyles } from "../../../theme/textfieldStyles";
import { useGetCatalogosQuery } from "../../../services/catalogs/catalogsService";
import {
  useGetBlByCodeBookingMutation,
  useLazyGetBlsCW1Query,
} from "../../../services/bl-export/BLExportService";
import FooterErrors from "./FooterErrors";
import {
  handleTouchedBlCW1Fields,
  handleTouchedCloneBl,
} from "../helpers/formHelpers";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setContainerToUpdate,
  setContainers,
  setIsFormUpdateContainer,
} from "../slices/BlExportSlice";
import { BLCW1 } from "../../../interfaces/bl-export/blexport.interface";
import {
  useGetClientsByUserQuery,
  useGetClientsQuery,
} from "../../../services/clients/clientsServices";
import ConfirmDialog from "../../../components/common/dialogs/ConfirmDialog";
import {
  createBlPromptMessage,
  editBlPromptMessage,
} from "../constants/constants";

interface props {
  submitAction: (values: CreateBLPayload) => void;
  blToClone?: BLValues;
  blToEdit?: BLValues;
}

const NewBlFormRF = ({ submitAction, blToClone, blToEdit }: props) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [openNotifyAditional, setOpenNotifyAditional] = useState(false);
  const [openContainersForm, setOpenContainersForm] = useState(false);
  const { containerToUpdate, containers } = useAppSelector(
    (state) => state.blExport
  );
  const [showSuccessBLCW1, setShowSuccessBLCW1] = useState(false);
  const [showPromptConfirmation, setShowPropmtConfirmation] = useState(false);

  // Inputs values for autocompletes ORIGIN - PORT LOADING - DESTINATION
  const [inputPortDischarge, setInputPortDischarge] = useState("");
  const [inputOrigin, setInputOrigin] = useState("");
  const [inputPortLoading, setInputPortLoading] = useState("");
  const [inputDestination, setInputDestination] = useState("");

  // Catalagos Options
  const { data: catalogs, isLoading: isLoadingCatalos } = useGetCatalogosQuery({
    page: 0,
    size: 10000,
    type: "CIUDAD",
  });

  const catalogsOptions = catalogs?.data?.map((ct) => ct.valor);

  // Empresas Options
  const { data: dataClientesAdmin, isLoading: isLoadingClientesAdmin } =
    useGetClientsQuery(
      {
        page: 0,
        size: 3000,
      },
      {
        skip: Boolean(userInfo?.perfil !== "1"),
      }
    );

  const optionsClientsAdmin =
    dataClientesAdmin?.data?.map((cl) => ({
      label: cl.nombre,
      value: cl.id,
    })) || [];

  // Empresas/Clientes by user Options
  const { data: dataClientes } = useGetClientsByUserQuery(
    {
      id_usuario: Number(userInfo!.id),
      page: 0,
      size: 100,
    },
    {
      skip: Boolean(userInfo?.perfil === "1"),
    }
  );

  const optionsClients =
    dataClientes?.data?.map((cl) => ({
      label: cl.nombre,
      value: cl.id,
    })) || [];

  // Success Form Fields Styles
  const classes = useTextfieldStyles();

  const handleOpenNotifyAditional = () => {
    setOpenNotifyAditional(!openNotifyAditional);
  };

  const handleOpenContainersForm = () => {
    setOpenContainersForm(true);
  };

  const handleCloseContainersForm = () => {
    dispatch(setContainerToUpdate(undefined));
    dispatch(setIsFormUpdateContainer(false));
    setOpenContainersForm(false);
  };

  const handleOpenSnackSuccessBLCW1 = () => {
    setShowSuccessBLCW1(true);
  };

  const handleCloseSnackSuccessBLCW1 = () => {
    setShowSuccessBLCW1(false);
  };

  // Querys
  const [
    searchBlByCodeBooking,
    { isLoading: isLoadingBlByCode, data: dataBlByCode },
  ] = useGetBlByCodeBookingMutation();

  const [
    searchBlCW1,
    { isLoading: isLoadingSearchBLCW1, data: dataSearchBLCW1 },
  ] = useLazyGetBlsCW1Query();

  /**
   * Returns initial values for the formik object
   * @returns Object
   */
  const getInitialValues = () => {
    if (blToClone !== undefined) {
      return blToClone;
    }
    if (blToEdit !== undefined) {
      return blToEdit;
    }
    return blInitialValues;
  };

  const {
    getFieldProps,
    errors,
    touched,
    values,
    setFieldValue,
    handleSubmit,
    setFieldTouched,
    isValid,
    setTouched,
  } = useFormik<BLValues>({
    initialValues: getInitialValues(),
    validationSchema: BlSchema,
    onSubmit: (values) => {
      if (containers.length === 0) {
        return alert("Bl no tiene contenedores");
      }
      submitAction({
        cabecera: values,
        detalles: containers,
      });
    },
  });

  // Return style classs if no erros & input touched
  const validateClassname = (keyName: string) => {
    const _touched: any = { ...touched };
    if (
      !(keyName in errors) &&
      _touched[`${keyName}`] &&
      _touched[`${keyName}`] === true
    ) {
      return classes.root;
    }
    return "";
  };

  const _submit = () => {
    setShowPropmtConfirmation(false);
    handleSubmit();
  };

  // Searches a given location in to  locations array
  const searchGiveLocationInLocationsArray = (givenLocation: string) => {
    const result = catalogsOptions?.find((opt) =>
      opt.includes(givenLocation.toUpperCase())
    );
    if (result !== undefined) {
      return result;
    }
    return String(givenLocation).toUpperCase();
  };

  const setValuesBlCW1Fields = async (valuesSearchBL: BLCW1) => {
    const customFields = JSON.parse(valuesSearchBL.customized_fields || "{}");
    await setFieldValue(
      "cod_booking",
      String(values.cod_booking).toUpperCase()
    );
    await setFieldValue(
      "from",
      searchGiveLocationInLocationsArray(valuesSearchBL.port_of_origin_name)
    );
    await setFieldValue(
      "ocean_vessel",
      String(valuesSearchBL.vessel_name).toUpperCase() +
        " " +
        String(valuesSearchBL.voyage_flight_no).toUpperCase()
    );
    await setFieldValue(
      "place_delivery",
      searchGiveLocationInLocationsArray(
        valuesSearchBL.port_of_destination_name
      )
    );
    await setFieldValue(
      "port_descharge",
      searchGiveLocationInLocationsArray(valuesSearchBL.port_of_discharge_name)
    );
    await setFieldValue(
      "port_loading",
      searchGiveLocationInLocationsArray(valuesSearchBL.port_of_loading_name)
    );
    if (Object.keys(customFields).length > 0 && "DAE" in customFields) {
      await setFieldValue("dae", String(customFields.DAE).trim());
    }
  };

  useEffect(() => {
    if (blToClone !== undefined) {
      dispatch(setContainers(blToClone.bl_details || []));
    }
    if (blToEdit !== undefined) {
      dispatch(setContainers(blToEdit.bl_details || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (blToEdit !== undefined) {
      return;
    }
    searchBlByCodeBooking({ cod_booking: values.cod_booking });
    if (values.cod_booking.length > 0) {
      searchBlCW1({ cod_booking: values.cod_booking });
    }
  }, [values.cod_booking, searchBlByCodeBooking, searchBlCW1, blToEdit]);

  useEffect(() => {
    if (dataSearchBLCW1?.datos && dataSearchBLCW1?.datos.length >= 1) {
      const valuesSearchBL = dataSearchBLCW1?.datos[0];
      handleOpenSnackSuccessBLCW1();
      handleTouchedBlCW1Fields(setTouched);
      setValuesBlCW1Fields(valuesSearchBL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearchBLCW1]);

  useEffect(() => {
    if (blToClone !== undefined || blToEdit !== undefined) {
      handleTouchedCloneBl(setTouched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blToClone, blToEdit]);

  useEffect(() => {
    if (containerToUpdate !== undefined) {
      setOpenContainersForm(true);
    }
  }, [containerToUpdate]);

  useEffect(() => {
    if (dataClientes?.data?.length && dataClientes.data.length > 0) {
      setFieldValue("id_cliente", dataClientes.data[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataClientes]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            className={
              Boolean(dataBlByCode !== undefined)
                ? ""
                : validateClassname("cod_booking")
            }
            {...getFieldProps("cod_booking")}
            onChange={(e) =>
              setFieldValue("cod_booking", String(e.target.value).toUpperCase())
            }
            variant="outlined"
            fullWidth
            label="Booking*"
            error={
              Boolean(errors.cod_booking && touched.cod_booking) ||
              Boolean(dataBlByCode !== undefined)
            }
            helperText={
              Boolean(dataBlByCode !== undefined)
                ? "El BL ya existe"
                : "Formato: Cualquier número o letra"
            }
            InputProps={{
              readOnly: Boolean(blToEdit !== undefined),
              endAdornment:
                isLoadingBlByCode || isLoadingSearchBLCW1 ? (
                  <CircularProgress size={20} />
                ) : null,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            className={validateClassname("ocean_vessel")}
            {...getFieldProps("ocean_vessel")}
            onChange={(e) =>
              setFieldValue(
                "ocean_vessel",
                String(e.target.value).toLocaleUpperCase()
              )
            }
            variant="outlined"
            fullWidth
            label="Ocean Vessel/Voyage*"
            error={Boolean(errors.ocean_vessel && touched.ocean_vessel)}
            helperText="Formato: Cualquier número o letra"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo
            forcePopupIcon
            options={catalogsOptions || []}
            noOptionsText="No hay resultados"
            value={
              values.port_descharge.length > 0 ? values.port_descharge : null
            }
            onChange={(e, val) => {
              if (val === null) {
                return setFieldValue("port_descharge", "");
              }
              setFieldValue("port_descharge", val);
            }}
            inputValue={inputPortDischarge}
            onInputChange={(e, val) => setInputPortDischarge(val.toUpperCase())}
            getOptionSelected={(opt, val) => Boolean(opt === val)}
            onBlur={() => {
              setFieldTouched("port_descharge");
              if (inputPortDischarge.length > 0) {
                setFieldValue("port_descharge", inputPortDischarge);
              }
            }}
            renderInput={(params) => (
              <TextField
                className={validateClassname("port_descharge")}
                {...params}
                variant="outlined"
                label="Port of Discharge"
                error={Boolean(errors.port_descharge && touched.port_descharge)}
                helperText="Formato: Cualquier número o letra"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCatalos ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo
            forcePopupIcon
            options={catalogsOptions || []}
            noOptionsText="No hay resultados"
            value={values.from.length > 0 ? values.from : null}
            onChange={(e, val) => {
              if (val === null) {
                return setFieldValue("from", "");
              }
              setFieldValue("from", val);
            }}
            getOptionSelected={(opt, val) => Boolean(opt === val)}
            inputValue={inputOrigin}
            onInputChange={(e, val) => setInputOrigin(val.toUpperCase())}
            onBlur={() => {
              setFieldTouched("from");
              if (inputOrigin.length > 0) {
                setFieldValue("from", inputOrigin);
              }
            }}
            renderInput={(params) => (
              <TextField
                className={validateClassname("from")}
                {...params}
                variant="outlined"
                label="Origin*"
                error={Boolean(errors.from && touched.from)}
                helperText="Formato: Cualquier número o letra"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCatalos ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo
            forcePopupIcon
            value={values.port_loading.length > 0 ? values.port_loading : null}
            options={catalogsOptions || []}
            noOptionsText="No hay resultados"
            onChange={(e, val) => {
              if (val === null) {
                return setFieldValue("port_loading", "");
              }
              setFieldValue("port_loading", val);
            }}
            getOptionSelected={(opt, val) => Boolean(opt === val)}
            inputValue={inputPortLoading}
            onInputChange={(e, val) => setInputPortLoading(val.toUpperCase())}
            onBlur={() => {
              setFieldTouched("port_loading");
              if (inputPortLoading.length > 0) {
                setFieldValue("port_loading", inputPortLoading);
              }
            }}
            renderInput={(params) => (
              <TextField
                className={validateClassname("port_loading")}
                {...params}
                variant="outlined"
                label="Port of Loading*"
                error={Boolean(errors.port_loading && touched.port_loading)}
                helperText="Formato: Cualquier número o letra"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCatalos ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            freeSolo={true}
            forcePopupIcon
            options={catalogsOptions || []}
            noOptionsText="No hay resultados"
            value={
              values.place_delivery.length > 0 ? values.place_delivery : null
            }
            onChange={(e, val) => {
              if (val === null) {
                return setFieldValue("place_delivery", "");
              }
              setFieldValue("place_delivery", val);
            }}
            inputValue={inputDestination}
            getOptionSelected={(opt, val) => opt === val}
            onInputChange={(e, val) =>
              setInputDestination(String(val).toUpperCase())
            }
            onBlur={() => {
              setFieldTouched("place_delivery");
              if (inputDestination.length > 0) {
                setFieldValue("place_delivery", inputDestination);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className={validateClassname("place_delivery")}
                variant="outlined"
                label="Destination"
                error={Boolean(errors.place_delivery && touched.place_delivery)}
                helperText="Formato: Cualquier número o letra"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCatalos ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            className={validateClassname("dae")}
            {...getFieldProps("dae")}
            variant="outlined"
            fullWidth
            label="DAE*"
            error={Boolean(errors.dae && touched.dae)}
            helperText="Formato: 028-2021-40-00523721"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={tipoBlOptions}
            onChange={(e, val) => setFieldValue("tipo_bl", val || "")}
            onBlur={() => setFieldTouched("tipo_bl")}
            value={values.tipo_bl.length > 0 ? values.tipo_bl : null}
            getOptionSelected={(opt, val) => Boolean(opt === val)}
            renderInput={(params) => (
              <TextField
                {...params}
                className={validateClassname("tipo_bl")}
                variant="outlined"
                label="Tipo BL*"
                error={Boolean(errors.tipo_bl && touched.tipo_bl)}
                helperText="Campo requerido"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={terminoFleteOptions}
            onChange={(e, val) => setFieldValue("formaPago", val || "")}
            onBlur={() => setFieldTouched("formaPago")}
            value={values.formaPago.length > 0 ? values.formaPago : null}
            getOptionSelected={(opt, val) => (opt === val ? true : false)}
            renderInput={(params) => (
              <TextField
                {...params}
                className={validateClassname("formaPago")}
                variant="outlined"
                label="Término de flete*"
                error={Boolean(errors.formaPago && touched.formaPago)}
                helperText="Campo requerido"
              />
            )}
          />
        </Grid>
        {userInfo?.perfil === "1" && isLoadingClientesAdmin === false ? (
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={optionsClientsAdmin.find(
                (opt) => opt.value === values.id_cliente
              )}
              options={optionsClientsAdmin}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              onChange={(_, val) => setFieldValue("id_cliente", val?.value)}
              disableClearable={true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Empresa"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingClientesAdmin ? (
                          <CircularProgress size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        ) : null}
        {optionsClients.length > 1 && (
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={optionsClients}
              defaultValue={optionsClients[0]}
              getOptionLabel={(opt) => opt.label}
              getOptionSelected={(opt, val) => opt.value === val.value}
              disableClearable={true}
              onChange={(_, val) => setFieldValue("id_cliente", val.value)}
              renderInput={(params) => (
                <TextField label="Empresa" variant="outlined" {...params} />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            variant="outlined"
            label="Shipper Reference"
            {...getFieldProps("shipper_reference")}
          />
        </Grid>
        <Grid item xs={12}>
          <u>
            <h3>Shipper</h3>
          </u>
          <TextField
            placeholder={placeholderShipper}
            variant="outlined"
            multiline
            fullWidth
            minRows={4}
            {...getFieldProps("info_shipper")}
          />
        </Grid>
        <Grid item xs={12}>
          <u>
            <h3>Consignee</h3>
          </u>
          <TextField
            placeholder={placeholderConsignee}
            variant="outlined"
            multiline
            fullWidth
            minRows={4}
            {...getFieldProps("info_consignee")}
          />
        </Grid>
        <Grid item xs={12}>
          <u>
            <h3>Notify Party</h3>
          </u>
          <TextField
            placeholder={placeholderNotify}
            variant="outlined"
            multiline
            fullWidth
            minRows={4}
            {...getFieldProps("info_notify")}
          />
        </Grid>
        <Grid item xs={12}>
          <ListItem button onClick={handleOpenNotifyAditional}>
            <ListItemText>
              <u>
                <Typography variant="body1" style={{ fontWeight: "bold" }}>
                  Notify Party Adicional
                </Typography>
              </u>
            </ListItemText>
            {openNotifyAditional ? <IconChevronUp /> : <IconChevronDown />}
          </ListItem>
          <Collapse in={openNotifyAditional} timeout="auto">
            <TextField
              placeholder={placeholderNotify}
              variant="outlined"
              multiline
              fullWidth
              minRows={4}
              {...getFieldProps("info_notifya")}
            />
          </Collapse>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" style={{ padding: "20px" }}>
            <Grid container>
              <Grid item xs={6}>
                <u>
                  <h3>Detalles Contenedores</h3>
                </u>
              </Grid>
              <Grid item xs={6}>
                <Collapse
                  in={!openContainersForm}
                  style={{
                    height: "100%",
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleOpenContainersForm}
                    startIcon={<IconPlus />}
                  >
                    Crear Contenedor
                  </Button>
                </Collapse>
              </Grid>
            </Grid>
            <br />
            <ContainersList />
            <br />
            <Collapse in={openContainersForm} timeout="auto">
              <NewContainerForm
                containers={containers}
                onCancel={handleCloseContainersForm}
                isEditParentBL={Boolean(blToEdit !== undefined)}
              />
            </Collapse>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <FooterErrors
            showErrorContainers={Boolean(containers.length === 0)}
            showErrorRequiredFields={Boolean(
              Object.entries(touched).length === 0 || isValid === false
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            disabled={Boolean(
              dataBlByCode !== undefined ||
                containers.length === 0 ||
                Object.entries(touched).length === 0 ||
                isValid === false
            )}
            startIcon={<IconDeviceFloppy />}
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={() => setShowPropmtConfirmation(true)}
          >
            Guardar
          </Button>
        </Grid>
        <Snackbar
          open={showSuccessBLCW1}
          onClose={handleCloseSnackSuccessBLCW1}
          autoHideDuration={5000}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          style={{ position: "absolute" }}
        >
          <Alert severity="success" variant="filled">
            Información de BL cargada correctamente
          </Alert>
        </Snackbar>
      </Grid>
      <ConfirmDialog
        isOpen={showPromptConfirmation}
        onCancel={() => {
          setShowPropmtConfirmation(false);
        }}
        text={
          blToEdit === undefined ? createBlPromptMessage : editBlPromptMessage
        }
        textConfirm="Sí, Continuar"
        onConfirm={_submit}
      />
    </div>
  );
};

export default NewBlFormRF;
